<template>
  <div class="g-page">
    <div class="content-container">

      <!-- HEADER -->
      <div class="header-row mb-4">
        <h2 class="page-title">{{ $t("Balans o'tkazish") }}</h2>
        <div class="header-actions">
          <v-select
            :clearable="false"
            class="modern-vselect sklad-select"
            :options="skladlist"
            v-model="sklad_id"
            :reduce="name => name.id"
            label="name"
          ></v-select>
        </div>
      </div>

      <!-- JORIY BALANS -->
      <div class="modern-card mb-3">
        <div class="section-header">
          <b-icon icon="wallet2"></b-icon>
          <span>{{ $t("Joriy balans") }}</span>
        </div>
        <div class="balance-grid">
          <div v-for="b in balances" :key="b.pay_type_id" class="balance-chip"
               :class="{ negative: b.balance < 0 }">
            <span class="bc-name">{{ b.name }}</span>
            <span class="bc-amount">{{ b.balance | numFormat }}</span>
          </div>
          <div v-if="balances.length === 0" class="text-muted">{{ $t("Ma'lumot topilmadi") }}</div>
        </div>
      </div>

      <!-- YANGI O'TKAZMA -->
      <div class="modern-card mb-3">
        <div class="section-header">
          <b-icon icon="arrow-left-right"></b-icon>
          <span>{{ $t("Yangi o'tkazma") }}</span>
        </div>

        <div class="row transfer-row align-items-start">
          <div class="col-md-4 col-12">
            <div class="form-unit">
              <label>{{ $t("Қайердан") }} <span class="req">*</span></label>
              <v-select
                :clearable="false"
                class="modern-vselect"
                :class="{ 'is-empty': !form.from_pay_type_id }"
                :options="paytypelist"
                v-model="form.from_pay_type_id"
                :reduce="name => name.id"
                label="name"
              ></v-select>
              <small v-if="fromBalance !== null" class="balance-hint">
                {{ $t('Балансда') }}: <b>{{ fromBalance | numFormat }}</b>
              </small>
            </div>
          </div>
          <div class="col-md-1 col-12 text-center arrow-col">
            <b-icon icon="arrow-right" font-scale="1.6"></b-icon>
          </div>
          <div class="col-md-4 col-12">
            <div class="form-unit">
              <label>{{ $t("Қайерга") }} <span class="req">*</span></label>
              <v-select
                :clearable="false"
                class="modern-vselect"
                :class="{ 'is-empty': !form.to_pay_type_id }"
                :options="paytypelist"
                v-model="form.to_pay_type_id"
                :reduce="name => name.id"
                label="name"
              ></v-select>
              <small v-if="toBalance !== null" class="balance-hint">
                {{ $t('Балансда') }}: <b>{{ toBalance | numFormat }}</b>
              </small>
            </div>
          </div>
          <div class="col-md-3 col-12">
            <div class="form-unit">
              <label>{{ $t('Сумма') }} <span class="req">*</span></label>
              <vue-numeric
                separator="space"
                v-model="form.summa"
                class="plain-input text-right"
                v-bind:precision="2"
              ></vue-numeric>
              <small v-if="convertedAmount !== null" class="balance-hint">
                Qabul qiluvchi hisobga: <b>{{ convertedAmount | numFormat }}</b>
                <span v-if="isDollarTransfer"> (kurs: {{ dollarRate | numFormat }})</span>
              </small>
            </div>
          </div>
        </div>

        <p v-if="sameTypeError" class="error-text">
          {{ $t("«Қайердан» ва «Қайерга» бир хил тўлов тури бўлмаслиги керак") }}
        </p>
        <p v-if="overBalanceWarning" class="warn-text">
          {{ $t("Diqqat: bu summa «Қайердан» balansidan katta, balans manfiy bo'lib qoladi") }}
        </p>

        <div class="row align-items-end">
          <div class="col-md-9 col-12">
            <div class="form-unit mb-0">
              <label>{{ $t('Комментарий') }}</label>
              <input type="text" class="plain-input" v-model="form.comment" :placeholder="$t('Ихтиёрий')">
            </div>
          </div>
          <div class="col-md-3 col-12 text-right">
            <button class="btn-save" type="button" :disabled="saving" @click="Save()">
              <b-icon icon="check-all" class="mr-1"></b-icon>{{ $t("Сақлаш") }}
            </button>
          </div>
        </div>
      </div>

      <!-- TARIX -->
      <div class="modern-card">
        <div class="section-header">
          <b-icon icon="clock-history"></b-icon>
          <span>{{ $t("O'tkazmalar tarixi") }}</span>
        </div>
        <div class="table-wrapper">
          <table class="custom-table">
            <thead>
              <tr>
                <th>{{ $t('Вақт') }}</th>
                <th>{{ $t('Склад') }}</th>
                <th>{{ $t("O'tkazma") }}</th>
                <th style="text-align:right">{{ $t('Сумма') }}</th>
                <th>{{ $t('Фойдаланувчи') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in history" :key="item.id">
                <td class="text-muted small">{{ $formatDate(item.datetime) }}</td>
                <td>{{ item.sklad ? item.sklad.name : '—' }}</td>
                <td>{{ item.comment }}</td>
                <td class="text-right font-bold">{{ item.transferSumma | numFormat }}</td>
                <td>{{ item.user ? item.user.first_name : '—' }}</td>
              </tr>
              <tr v-if="history.length === 0">
                <td colspan="5" class="empty-row">{{ $t("Ma'lumot topilmadi") }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

    </div>
  </div>
</template>
<script>
import { print } from "../../components/mixins/print.js";

const TRANSFER_TAG = "Balans o'tkazish";

export default {
  name: "BalansTransferIndex",
  mixins: [print],
  data() {
    return {
      saving: false,
      sklad_id: parseInt(localStorage.getItem('sklad_id')) || null,
      skladlist: [],
      paytypelist: [],
      balances: [],
      history: [],
      dollarRate: 1,
      baseId: null,
      categoryId: null,
      form: {
        from_pay_type_id: null,
        to_pay_type_id: null,
        summa: 0,
        comment: "",
      }
    };
  },
  computed: {
    sameTypeError() {
      return !!(this.form.from_pay_type_id && this.form.to_pay_type_id
        && this.form.from_pay_type_id == this.form.to_pay_type_id);
    },
    fromBalance() {
      if (!this.form.from_pay_type_id) return null;
      const b = this.balances.find(x => x.pay_type_id == this.form.from_pay_type_id);
      return b ? b.balance : 0;
    },
    toBalance() {
      if (!this.form.to_pay_type_id) return null;
      const b = this.balances.find(x => x.pay_type_id == this.form.to_pay_type_id);
      return b ? b.balance : 0;
    },
    overBalanceWarning() {
      return this.fromBalance !== null && parseFloat(this.form.summa || 0) > this.fromBalance;
    },
    isDollarTransfer() {
      return this.form.from_pay_type_id == 3 || this.form.to_pay_type_id == 3;
    },
    convertedAmount() {
      const summa = parseFloat(this.form.summa || 0);
      if (!summa || !this.form.from_pay_type_id || !this.form.to_pay_type_id) return null;
      if (this.form.from_pay_type_id == 3 && this.form.to_pay_type_id != 3) {
        return Math.round(summa * this.dollarRate * 100) / 100;
      }
      if (this.form.from_pay_type_id != 3 && this.form.to_pay_type_id == 3) {
        return Math.round((summa / this.dollarRate) * 1000) / 1000;
      }
      return summa;
    }
  },
  watch: {
    sklad_id() {
      localStorage.setItem('sklad_id', this.sklad_id);
      this.LoadBalances();
      this.LoadHistory();
    }
  },
  mounted() {
    this.Skladlist();
    this.Paytypelist();
    this.LoadBalances();
    this.LoadHistory();
    this.LoadDollarRate();
  },
  methods: {
    Skladlist() {
      let self = this;
      axios.get("api/v1/sklads").then(function (response) {
        self.skladlist = response.data;
      });
    },
    Paytypelist() {
      let self = this;
      axios.get("api/v1/pay-type").then(function (response) {
        self.paytypelist = response.data;
      });
    },
    async LoadDollarRate() {
      try {
        const response = await axios.get("api/v1/dollar-exchange-rate/last");
        this.dollarRate = parseFloat(response.data && response.data.rate) || 1;
      } catch (e) {
        this.dollarRate = 1;
      }
    },
    LoadBalances() {
      let self = this;
      if (!self.sklad_id) return;
      axios.get("api/v1/kassa-order/balance-by-paytype", { params: { sklad_id: self.sklad_id } })
        .then(function (response) {
          self.balances = response.data;
        });
    },
    LoadHistory() {
      let self = this;
      axios.get("api/v1/kassa-order").then(function (response) {
        self.history = (response.data || [])
          .filter(item => item.kassa && item.kassa.name === TRANSFER_TAG
            && (!self.sklad_id || item.sklad_id == self.sklad_id))
          .map(item => ({ ...item, transferSumma: Math.abs(parseFloat(item.summa) || parseFloat(item.dollar_summa) || 0) }))
          .sort((a, b) => b.datetime - a.datetime);
      });
    },
    // "Asos" (Base) va "Kassa Category" — foydalanuvchidan yashirilgan, faqat
    // kassa-order backendi talab qilgani uchun bir marta topiladi/yaratiladi.
    async EnsureBaseId() {
      if (this.baseId) return this.baseId;
      const res = await axios.get("api/v1/base");
      let found = (res.data || []).find(b => b.name === TRANSFER_TAG);
      if (!found) {
        const created = await axios.post("api/v1/base", { name: TRANSFER_TAG });
        found = created.data;
      }
      this.baseId = found.id;
      return this.baseId;
    },
    async EnsureCategoryId() {
      if (this.categoryId) return this.categoryId;
      const res = await axios.get("api/v1/kassa-category");
      const list = (res.data && res.data.data) || [];
      let found = list.find(c => c.name === TRANSFER_TAG);
      if (!found) {
        const created = await axios.post("api/v1/kassa-category", { name: TRANSFER_TAG });
        found = created.data && created.data.data;
      }
      this.categoryId = found.id;
      return this.categoryId;
    },
    Validate() {
      if (!this.sklad_id) {
        this.$store.state.errors = this.$t("Склад танланг");
        return false;
      }
      if (!this.form.from_pay_type_id || !this.form.to_pay_type_id) {
        this.$store.state.errors = this.$t("«Қайердан» ва «Қайерга» тўлов турларини танланг");
        return false;
      }
      if (this.sameTypeError) {
        this.$store.state.errors = this.$t("«Қайердан» ва «Қайерга» бир хил тўлов тури бўлмаслиги керак");
        return false;
      }
      if (!this.form.summa || parseFloat(this.form.summa) <= 0) {
        this.$store.state.errors = this.$t("Суммани киритинг");
        return false;
      }
      if (this.isDollarTransfer && !(this.dollarRate > 1)) {
        this.$store.state.errors = "Dollar kursini yuklab bo'lmadi";
        return false;
      }
      return true;
    },
    async Save() {
      if (this.rule.change != true) {
        this.$store.state.errors = this.$t('Sizdan bunday xuquq mavjud emas');
        return;
      }
      if (!this.Validate()) return;
      let self = this;
      self.saving = true;
      try {
        const [baseId, categoryId] = await Promise.all([this.EnsureBaseId(), this.EnsureCategoryId()]);
        const summa = parseFloat(self.form.summa);
        const convertedSumma = self.convertedAmount;
        const fromName = (self.paytypelist.find(p => p.id == self.form.from_pay_type_id) || {}).name || '';
        const toName = (self.paytypelist.find(p => p.id == self.form.to_pay_type_id) || {}).name || '';
        const autoComment = fromName + ' → ' + toName + (self.form.comment ? (' (' + self.form.comment + ')') : '');

        const rows = [
          { base_id: baseId, pay_type_id: self.form.from_pay_type_id, type: false, summa, comment: autoComment },
          { base_id: baseId, pay_type_id: self.form.to_pay_type_id, type: true, summa: convertedSumma, comment: autoComment },
        ];

        let totalSumma = 0;
        let totalDollar = 0;
        rows.forEach(function (row) {
          const signed = row.type ? row.summa : -row.summa;
          if (row.pay_type_id == 3) {
            totalDollar += signed;
          } else {
            totalSumma += signed;
          }
        });

        const payload = {
          sklad_id: self.sklad_id,
          datetime: Math.floor(Date.now() / 1000),
          kassa_category_id: categoryId,
          summa: totalSumma,
          dollar_summa: totalDollar,
          dollar_rate: self.dollarRate,
          comment: autoComment,
          kassa_order_table: rows,
        };

        await axios({ method: "post", url: "api/v1/kassa-order", data: payload });

        self.form.from_pay_type_id = null;
        self.form.to_pay_type_id = null;
        self.form.summa = 0;
        self.form.comment = "";
        self.LoadBalances();
        self.LoadHistory();
      } catch (error) {
        self.$store.state.errors = (error.response && error.response.data && error.response.data.message)
          || self.$t("Xatolik yuz berdi");
      } finally {
        self.saving = false;
      }
    }
  }
};
</script>
<style scoped>
.g-page {
  padding: 24px;
  background: #f4f7f9;
  min-height: 100vh;
}

.content-container {
  max-width: 1100px;
  margin: 0 auto;
}

.header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.page-title {
  font-weight: 800;
  font-size: 24px;
  color: #2c3e50;
  margin: 0;
}

.sklad-select {
  min-width: 240px;
}

.modern-card {
  background: white;
  border-radius: 20px;
  padding: 24px 28px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.02);
  border: 1px solid #f1f2f6;
}

.mb-3 { margin-bottom: 18px; }
.mb-4 { margin-bottom: 24px; }

.section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 18px;
  color: #2c3e50;
  font-weight: 700;
  font-size: 15px;
}

.section-header svg {
  color: #a142f4;
}

.balance-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.balance-chip {
  background: #f0fdf4;
  border: 1px solid #dcfce7;
  border-radius: 12px;
  padding: 10px 16px;
  min-width: 150px;
}

.balance-chip.negative {
  background: #fef2f2;
  border-color: #fee2e2;
}

.bc-name {
  display: block;
  font-size: 11px;
  color: #94a3b8;
  text-transform: uppercase;
  font-weight: 700;
  margin-bottom: 4px;
}

.bc-amount {
  display: block;
  font-size: 17px;
  font-weight: 800;
  color: #16a34a;
}

.balance-chip.negative .bc-amount {
  color: #e11d48;
}

.transfer-row {
  background: #f8fafc;
  border-radius: 14px;
  padding: 16px 16px 4px 16px;
  margin-bottom: 6px;
}

.arrow-col {
  color: #a142f4;
  padding-top: 30px;
}

.form-unit {
  margin-bottom: 18px;
}

.form-unit.mb-0 {
  margin-bottom: 0;
}

.form-unit label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  color: #95a5a6;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.plain-input {
  width: 100%;
  background: #f8fafc;
  border: 1px solid #f8fafc;
  border-radius: 10px;
  padding: 9px 13px;
  color: #2c3e50;
  font-weight: 600;
}

.plain-input:focus {
  background: white;
  border-color: #a142f4;
  outline: none;
}

.req {
  color: #e11d48;
  font-weight: 800;
}

.error-text {
  color: #e11d48;
  font-weight: 600;
  font-size: 13px;
  margin: -6px 0 14px 4px;
}

.warn-text {
  color: #d97706;
  font-weight: 600;
  font-size: 13px;
  margin: -6px 0 14px 4px;
}

.balance-hint {
  display: block;
  margin-top: 4px;
  color: #64748b;
}

.btn-save {
  background: #a142f4;
  color: white;
  border: none;
  padding: 10px 22px;
  border-radius: 10px;
  font-weight: 700;
  box-shadow: 0 4px 12px rgba(161, 66, 244, 0.25);
  cursor: pointer;
}

.btn-save:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.table-wrapper {
  overflow-x: auto;
}

.custom-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

.custom-table th {
  padding: 12px 10px;
  color: #94a3b8;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  border-bottom: 1px solid #f1f5f9;
  text-align: left;
}

.custom-table td {
  padding: 12px 10px;
  border-bottom: 1px solid #f8fafc;
  font-size: 14px;
  color: #334155;
}

.text-right {
  text-align: right;
}

.font-bold {
  font-weight: 700;
}

.empty-row {
  text-align: center;
  padding: 40px;
  color: #94a3b8;
  font-style: italic;
}

::v-deep .modern-vselect .vs__dropdown-toggle {
  background: #f8fafc;
  border: 1px solid #eef2f7;
  border-radius: 10px;
  padding: 3px 4px;
}

::v-deep .modern-vselect.is-empty .vs__dropdown-toggle {
  border-color: #fca5a5;
  background: #fff5f5;
}

@media (max-width: 768px) {
  .modern-card {
    padding: 18px;
  }
}
</style>
